import type { Viewer } from '@cesiumgs/cesium-analytics'
import { CallbackProperty, Color, ConstantPositionProperty, Entity, Rectangle, ScreenSpaceEventType, defined } from '@cesiumgs/cesium-analytics'
import DrawAction from './DrawAction'
import { type DrawOptions } from './DrawOptions'

/**
 * Cesium Tool for Draw Rectangle
 * @example
 * ```typescript
 * let draw=new Draw(view);
 * draw.create(DrawType.Rect,null);
 * draw.on(DrawEventType.CursorUpdate,evt=>{
 * });
 * ```
 */
export default class DrawRectAction extends DrawAction {
  //  origin:Cesium.Cartesian3;
  // edgePoint:Cesium.Cartesian3;
  /**
   * @internal
   */
  rectEntity: Entity | undefined
  /**
   * @internal
   */
  originProperty: ConstantPositionProperty = new ConstantPositionProperty()
  /**
   * @internal
   */
  rectProperty: ConstantPositionProperty | undefined
  /**
   * @internal
   */
  vertexCompleteHandler(movement: any): void {
    // this.viewer.trackedEntity = undefined;
    //   this.renderResult = this.drawResults?.postions;
    this.removeListener()
    this.complete()
    this.state = 'end'
  }

  /**
   * @internal
   */
  cursorUpdateHandler(movement: any) {
    if (this.state === 'start') {
      const ray = this.viewer.camera.getPickRay(movement.endPosition)
      if (ray) {
        const position = this.viewer.scene.globe.pick(ray, this.viewer.scene)
        if (defined(position) && position) {
          if (this.drawResults.postions.length === 2) {
            this.drawResults.postions.splice(1, 1, position)
          }
          else {
            this.drawResults.postions.push(position)
          }
          this.updateEvt.raiseEvent(this.drawResults as any)
        }
      }
    }
  }

  /**
   * @internal
   */
  vertexAddHandler(movement: any): void {
    if (this.state === 'start')
      return
    if (this.state === 'end') {
      this.state = 'start'
    }
    const ray = this.viewer.camera.getPickRay(movement.position)
    if (ray) {
      const position = this.viewer.scene.globe.pick(ray, this.viewer.scene)
      if (position) {
        this.originProperty.setValue(position)
        this.drawResults?.postions.splice(0, 1, position)
      }
      this.addEvt.raiseEvent(this.drawResults as any)
    }
  }

  /**
   * @internal
   */
  registerViewerEvent(): void {
    this.vertexAddHandlerInstance?.setInputAction(
      this.vertexAddHandler.bind(this),
      ScreenSpaceEventType.LEFT_CLICK,
    )
    this.cursorUpdateHandlerInstance?.setInputAction(
      this.cursorUpdateHandler.bind(this),
      ScreenSpaceEventType.MOUSE_MOVE,
    )
    this.vertexCompleteHandlerInstance?.setInputAction(
      this.vertexCompleteHandler.bind(this),
      ScreenSpaceEventType.RIGHT_CLICK,
    )
  }

  /**
   * @internal
   */
  removeListener(): void {
    this.vertexAddHandlerInstance?.removeInputAction(
      ScreenSpaceEventType.LEFT_CLICK,
    )
    this.cursorUpdateHandlerInstance?.removeInputAction(
      ScreenSpaceEventType.MOUSE_MOVE,
    )
    this.vertexCompleteHandlerInstance?.removeInputAction(
      ScreenSpaceEventType.RIGHT_CLICK,
    )
  }

  /**
   * @internal
   */
  constructor(viewer: Viewer, options?: DrawOptions) {
    super(viewer, options)

    this.type = 'rect'

    if (this.drawoptions?.useDefaultEntity) {
      this.originProperty = new ConstantPositionProperty()
      this.rectProperty = new ConstantPositionProperty()
      const entity
        = this.drawoptions.defaultCircleEntity
        || new Entity({
          position: this.originProperty,
          rectangle: {
            coordinates: new CallbackProperty(() => {
              if (this.drawResults.postions.length === 2) {
                return Rectangle.fromCartesianArray(
                  this.drawResults.postions,
                )
              }
            }, false),
            material: Color.fromCssColorString('#FFD040'),
          },
        })
      this.rectEntity = this.dataSource.entities.add(entity)
    }
  }
}
